import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { SearchStateService } from './search-state.service';
import { DirectBookingService } from '../direct-booking/direct-booking.service';
import { LanguageService } from '../i18n/language.service';

const WEEKDAYS: Record<string, string[]> = {
  es: ['lu', 'ma', 'mi', 'ju', 'vi', 'sa', 'do'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
  pt: ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'],
};
const MONTHS: Record<string, string[]> = {
  es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  pt: ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
};

export interface CalDay {
  iso: string;
  label: number;
  inMonth: boolean;
  selectable: boolean;
  selected: boolean;
  inRange: boolean;
  unavailable: boolean;
}

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Global date/guests overlays + a working month calendar bound to SearchStateService. */
@Injectable({ providedIn: 'root' })
export class SearchOverlayService {
  readonly showDest = signal(false);
  readonly showCal = signal(false);
  readonly showGuests = signal(false);

  private readonly state = inject(SearchStateService);
  private readonly booking = inject(DirectBookingService);
  private readonly language = inject(LanguageService);
  private readonly cursor = signal(this.initialCursor());

  readonly weekdays = computed(() => {
    const lang = this.language.activeLang();
    return WEEKDAYS[lang] ?? WEEKDAYS['es'];
  });

  /** Apartment whose Hostify calendar drives availability (null = no constraint). */
  private readonly apartmentId = signal<string | null>(null);
  /** ISO dates that are booked/unavailable in the PMS. */
  private readonly unavailable = signal<Set<string>>(new Set());

  constructor() {
    // Load real PMS availability for the visible window whenever the apartment
    // or the month changes; merge so navigating months keeps prior data.
    effect(() => {
      const id = this.apartmentId();
      const { year, month } = this.cursor();
      if (!id) return;
      const from = iso(year, month, 1);
      const toDate = new Date(year, month + 3, 0); // ~3 months ahead
      const to = iso(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
      this.booking.getAvailability(id, from, to).subscribe((res) => {
        this.unavailable.update((prev) => {
          const next = new Set(prev);
          for (const d of res.days ?? []) {
            if (d.status && d.status !== 'available') next.add(d.date);
          }
          return next;
        });
        // If the pre-selected range now overlaps a blocked night, clear it so the
        // user picks valid dates against the real PMS calendar.
        const f = this.state.from();
        const t = this.state.to();
        if (f && t && this.rangeHasBlocked(f, t)) {
          this.state.to.set(null);
        }
      });
    });
  }

  /** Point the calendar at a property so it reflects that listing's PMS calendar. */
  setApartment(id: string | null): void {
    if (id !== this.apartmentId()) {
      this.unavailable.set(new Set());
      this.apartmentId.set(id);
    }
  }

  readonly calTitle = computed(() => {
    const c = this.cursor();
    const lang = this.language.activeLang();
    return `${MONTHS[lang]?.[c.month] ?? MONTHS['es'][c.month]} ${c.year}`;
  });

  readonly calDays = computed<CalDay[]>(() => {
    const { year, month } = this.cursor();
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // make Monday = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayIso = iso(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const from = this.state.from();
    const to = this.state.to();
    const blocked = this.unavailable();

    const cells: CalDay[] = [];
    for (let i = 0; i < 42; i++) {
      const dayNum = i - startOffset + 1;
      const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
      const dateIso = inMonth ? iso(year, month, dayNum) : '';
      const unavailable = inMonth && blocked.has(dateIso);
      const selectable = inMonth && dateIso >= todayIso && !unavailable;
      const selected = inMonth && (dateIso === from || dateIso === to);
      const inRange = inMonth && !!from && !!to && dateIso > from && dateIso < to;
      cells.push({
        iso: dateIso,
        label: inMonth ? dayNum : 0,
        inMonth,
        selectable,
        selected,
        inRange,
        unavailable,
      });
    }
    return cells;
  });

  toggleDest(): void {
    this.showDest.update((v) => !v);
  }
  toggleCal(): void {
    this.showCal.update((v) => !v);
    this.showGuests.set(false);
    this.showDest.set(false);
  }
  toggleGuests(): void {
    this.showGuests.update((v) => !v);
    this.showCal.set(false);
    this.showDest.set(false);
  }
  closeAll(): void {
    this.showDest.set(false);
    this.showCal.set(false);
    this.showGuests.set(false);
  }

  prevMonth(): void {
    this.cursor.update((c) => {
      const m = c.month - 1;
      return m < 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: m };
    });
  }
  nextMonth(): void {
    this.cursor.update((c) => {
      const m = c.month + 1;
      return m > 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: m };
    });
  }

  /** Range selection: first click sets check-in, second sets check-out. */
  pickDay(day: CalDay): void {
    if (!day.selectable) return;
    const from = this.state.from();
    const to = this.state.to();
    if (!from || (from && to)) {
      this.state.from.set(day.iso);
      this.state.to.set(null);
    } else if (day.iso <= from) {
      this.state.from.set(day.iso);
    } else if (this.rangeHasBlocked(from, day.iso)) {
      // The selected check-out would span a booked/unavailable night → restart.
      this.state.from.set(day.iso);
      this.state.to.set(null);
    } else {
      this.state.to.set(day.iso);
    }
  }

  /** True if any night in [from, to) is blocked in the PMS calendar. */
  private rangeHasBlocked(from: string, to: string): boolean {
    const blocked = this.unavailable();
    if (blocked.size === 0) return false;
    const d = new Date(from + 'T00:00:00');
    const end = new Date(to + 'T00:00:00');
    while (d < end) {
      const i = iso(d.getFullYear(), d.getMonth(), d.getDate());
      if (blocked.has(i)) return true;
      d.setDate(d.getDate() + 1);
    }
    return false;
  }

  private initialCursor(): { year: number; month: number } {
    const ref = this.state.from() ? new Date(this.state.from()! + 'T00:00:00') : new Date();
    return { year: ref.getFullYear(), month: ref.getMonth() };
  }
}
